/* eslint-disable no-plusplus */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from 'react';

import api from '../api/api';
import {
 WidgetCamerasProps,
 WidgetDriverTerminalProps,
 WidgetDestPointNetoProps,
 WidgetInOutProductProps,
 WidgetItemType,
 WidgetOnlineWeighingProps,
 WidgetProductStockProps,
 WidgetProgressProps,
} from '../components/dashboard/Dashboard.types';

const useDashboardWidget = (
 widget:
  | WidgetInOutProductProps
  | WidgetDestPointNetoProps
  | WidgetProgressProps
  | WidgetProductStockProps
  | WidgetOnlineWeighingProps
  | WidgetCamerasProps
  | WidgetDriverTerminalProps,
) => {
 const [widgetContent, setWidgetContent] = useState<WidgetItemType[]>([]);
 const [isLoading, setIsLoading] = useState(false);
 const totalSum = useMemo(
  () =>
   widgetContent.reduce(
    (acc, item) =>
     acc +
     (item.Aggregates['CargoNeto.sum'] ??
      item.Aggregates['neto.sum'] ??
      item.Aggregates['Neto.sum'] ??
      0),
    0,
   ),
  [widgetContent],
 );
 const fetchWidget = useCallback(
  async (data: any, sortOrder: 'asc' | 'desc' = 'desc') => {
   setIsLoading(true);
   const response = await api.post('/Results/v2/', data).finally(() => {
    setIsLoading(false);
   });
   if (!response.data) {
    setWidgetContent([]);
    return;
   }
   const sortedData = response.data.sort((a: any, b: any) => {
    const aValue =
     a.Aggregates['CargoNeto.sum'] ??
     a.Aggregates['neto.sum'] ??
     a.Aggregates['Neto.sum'] ??
     0;
    const bValue =
     b.Aggregates['CargoNeto.sum'] ??
     b.Aggregates['neto.sum'] ??
     b.Aggregates['Neto.sum'] ??
     0;
    const diff = bValue - aValue;
    return sortOrder === 'asc' ? -diff : diff;
   });
   setWidgetContent(sortedData);
  },
  [],
 );
 useEffect(() => {
  // Online weighing / Cameras / Driver Terminal have no /Results data — skip fetch.
  if (
   widget.type === 'onlineWeighing' ||
   widget.type === 'cameras' ||
   widget.type === 'driverTerminal'
  ) {
   setWidgetContent([]);
   setIsLoading(false);
   return;
  }
  let fetchParams = {};
  const fetchFilters = [];
  // if (
  //  widget.type !== 'driversProgress' &&
  //  widget.type !== 'destPointNeto' &&
  //  widget.type !== 'InOutProduct'
  // ) {
  //  setWidgetContent([]);
  //  setIsLoading(false);
  //  return;
  // }
  // GERIAUSI VAIRUOTOJAI
  if (widget.type === 'driversProgress') {
   const progressWidget = widget as WidgetProgressProps;
   // Add Sender filter if provided
   if (progressWidget.sender?.Id) {
    fetchFilters.push({
     field: 'Sender.Id',
     operator: 'eq',
     value: progressWidget.sender.Id,
     logic: 'And',
    });
   }
   // Add Receiver filter if provided
   if (progressWidget.receiver?.Id) {
    fetchFilters.push({
     field: 'Receiver.Id',
     operator: 'eq',
     value: progressWidget.receiver.Id,
     logic: 'And',
    });
   }
   // Add Driver filter if provided
   if (progressWidget.driver?.Id) {
    fetchFilters.push({
     field: 'Driver.Id',
     operator: 'eq',
     value: progressWidget.driver.Id,
     logic: 'And',
    });
   }

   fetchParams = {
    groupBy: ['Driver.FullName', 'sum(neto)'], // 'month(Date)',
    fields: 'Date,Neto, Driver.FullName',
    limit: 10000,
   };
   if (progressWidget.dateFrom) {
    fetchFilters.push({
     field: 'Date',
     operator: 'gte',
     value: progressWidget.dateFrom,
     logic: 'And',
    });
   }
   if (progressWidget.dateTo) {
    fetchFilters.push({
     field: 'Date',
     operator: 'lte',
     value: progressWidget.dateTo,
     logic: 'And',
    });
   }
  } else if (widget.type === 'destPointNeto') {
   const destPointWidget = widget as WidgetDestPointNetoProps;
   // Add Products filter if provided
   if (destPointWidget.products?.Id) {
    fetchFilters.push({
     field: 'ResultProducts.Product.Id',
     operator: 'eq',
     value: destPointWidget.products.Id,
     logic: 'And',
    });
   }

   if (destPointWidget.toDestPoint) {
    if (Array.isArray(destPointWidget.toDestPoint)) {
     if (destPointWidget.toDestPoint.length > 0) {
      fetchFilters.push({
       field: 'ToDestPoint.Id',
       operator: 'eq',
       value: destPointWidget.toDestPoint.map((item) => item.Id),
       logic: 'And',
      });
     }
    } else if (destPointWidget.toDestPoint.Id) {
     fetchFilters.push({
      field: 'ToDestPoint.Id',
      operator: 'eq',
      value: destPointWidget.toDestPoint.Id,
      logic: 'And',
     });
    }
   }

   fetchParams = {
    groupBy: ['ToDestPoint.Name', 'year(Date)', 'sum(CargoNeto)'], // 'month(Date)',
    fields: 'Date,Neto,CargoNeto, ToDestPoint.Name',
    limit: 10000,
   };
   if (destPointWidget.dateFrom) {
    fetchFilters.push({
     field: 'Date',
     operator: 'gte',
     value: destPointWidget.dateFrom,
     logic: 'And',
    });
   }
   if (destPointWidget.dateTo) {
    fetchFilters.push({
     field: 'Date',
     operator: 'lte',
     value: destPointWidget.dateTo,
     logic: 'And',
    });
   }
   // IŠVEŽTA IR ATVEŽTA
  } else if (widget.type === 'InOutProduct') {
   const inOutWidget = widget as WidgetInOutProductProps;

   if (inOutWidget.products?.Id) {
    fetchFilters.push({
     field: 'ResultProducts.Product.Id',
     operator: 'eq',
     value: inOutWidget.products.Id,
     logic: 'And',
    });
   }
   if (inOutWidget.productGroups?.Id) {
    fetchFilters.push({
     field: 'ResultProducts.Product.ProductGroup.Id',
     operator: 'eq',
     value: inOutWidget.productGroups.Id,
     logic: 'And',
    });
   }
   if (inOutWidget.toDestPoint?.Id) {
    fetchFilters.push({
     field: 'ToDestPoint.Id',
     operator: 'eq',
     value: inOutWidget.toDestPoint.Id,
     logic: 'And',
    });
   }
   if (inOutWidget.destPointGroup?.Id) {
    fetchFilters.push({
     field: 'ToDestPoint.DestPointGroup.Id',
     operator: 'eq',
     value: inOutWidget.destPointGroup.Id,
     logic: 'And',
    });
   }
   if (inOutWidget.division?.Id) {
    fetchFilters.push({
     field: 'Division.Id',
     operator: 'eq',
     value: inOutWidget.division.Id,
     logic: 'And',
    });
   }
   // If filters is empty, do not send it in the fetchWidget call.
   fetchParams = {
    groupBy: ['Cargo', 'year(Date)', 'sum(Neto)'],
    fields: 'Cargo,Date,Neto',
    limit: 1000,
   };
   if (inOutWidget.dateFrom) {
    fetchFilters.push({
     field: 'Date',
     operator: 'gte',
     value: inOutWidget.dateFrom,
     logic: 'And',
    });
   }
   if (inOutWidget.dateTo) {
    fetchFilters.push({
     field: 'Date',
     operator: 'lte',
     value: inOutWidget.dateTo,
     logic: 'And',
    });
   }
  } else if (widget.type === 'ProductStock') {
   const productStockWidget = widget as WidgetProductStockProps;

   if (productStockWidget.fromDestPoint?.Id) {
    fetchFilters.push({
     field: 'FromDestPoint.Id',
     operator: 'eq',
     value: productStockWidget.fromDestPoint.Id,
     logic: 'And',
    });
   }

   if (productStockWidget.products?.Id) {
    fetchFilters.push({
     field: 'ResultProducts.Product.Id',
     operator: 'eq',
     value: productStockWidget.products.Id,
     logic: 'And',
    });
   }

   if (productStockWidget.toDestPoint?.Id) {
    fetchFilters.push({
     field: 'ToDestPoint.Id',
     operator: 'eq',
     value: productStockWidget.toDestPoint.Id,
     logic: 'And',
    });
   }

   if (productStockWidget.toDestPointGroups) {
    if (Array.isArray(productStockWidget.toDestPointGroups)) {
     if (productStockWidget.toDestPointGroups.length > 0) {
      fetchFilters.push({
       field: 'ToDestPoint.DestPointGroup.Id',
       operator: 'eq',
       value: productStockWidget.toDestPointGroups.map((item) => item.Id),
       logic: 'And',
      });
     }
    } else if (productStockWidget.toDestPointGroups.Id) {
     fetchFilters.push({
      field: 'ToDestPoint.DestPointGroup.Id',
      operator: 'eq',
      value: productStockWidget.toDestPointGroups.Id,
      logic: 'And',
     });
    }
   }

   fetchParams = {
    groupBy: ['ResultProducts.Product.Name', 'sum(CargoNeto)'],
    fields: 'Date,Neto,CargoNeto, ResultProducts.Product.Name',
    limit: 10000,
   };
   if (productStockWidget.dateFrom) {
    fetchFilters.push({
     field: 'Date',
     operator: 'gte',
     value: productStockWidget.dateFrom,
     logic: 'And',
    });
   }
   if (productStockWidget.dateTo) {
    fetchFilters.push({
     field: 'Date',
     operator: 'lte',
     value: productStockWidget.dateTo,
     logic: 'And',
    });
   }
  }

  if (fetchFilters.length > 0) {
   fetchFilters.forEach((filter) => {
    if ('logic' in filter) {
     // eslint-disable-next-line no-param-reassign
     delete (filter as any).logic;
    }
   });
   // Step 2: Add 'logic: "And"' to all but the last filter.
   for (let i = 0; i < fetchFilters.length - 1; i++) {
    fetchFilters[i].logic = 'And';
   }

   fetchParams = {
    ...fetchParams,
    filters: fetchFilters,
   };
  }

  const sortOrder =
   (widget as WidgetProgressProps).sortOrder ??
   (widget as WidgetProductStockProps).sortOrder ??
   'desc';
  fetchWidget(fetchParams, sortOrder);
 }, [
  fetchWidget,
  widget.dateFrom,
  widget.dateTo,
  widget.type,
  widget.lastUpdated,
  (widget as WidgetProgressProps).sender?.Id,
  (widget as WidgetProgressProps).receiver?.Id,
  (widget as WidgetProgressProps).driver?.Id,
  (widget as WidgetProgressProps).products?.Id,
  (widget as WidgetProgressProps).sortOrder,
  (widget as WidgetDestPointNetoProps).products?.Id,
  (widget as WidgetDestPointNetoProps).toDestPoint,
  (widget as WidgetInOutProductProps).products?.Id,
  (widget as WidgetInOutProductProps).toDestPoint?.Id,
  (widget as WidgetInOutProductProps).destPointGroup?.Id,
  (widget as WidgetInOutProductProps).division?.Id,
  (widget as WidgetProductStockProps).fromDestPoint?.Id,
  (widget as WidgetProductStockProps).products?.Id,
  (widget as WidgetProductStockProps).toDestPoint?.Id,
  (widget as WidgetProductStockProps).toDestPointGroups,
  (widget as WidgetProductStockProps).sortOrder,
 ]);
 return {
  widgetContent,
  isLoading,
  totalSum,
 };
};
export default useDashboardWidget;

